// Thin fetch wrapper around the FastAPI backend. Paths are relative: in dev, CRA's "proxy" field in
// package.json forwards them to http://localhost:5000; in production the React build is served by the
// same FastAPI process, so relative paths resolve to the same origin either way.

async function asJson(resp) {
  if (!resp.ok) {
    // A 401 anywhere means the session is gone (expired, logged out in another tab, or the user was
    // deleted). Broadcast it so AuthProvider can drop back to the login screen instead of each caller
    // having to handle it. /me 401s on first load too, which is expected and harmless here.
    if (resp.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth-expired"));
    }
    let detail = resp.statusText;
    try {
      const d = (await resp.json()).detail;
      detail = (Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d) || detail;
    } catch {
      // body wasn't JSON — keep statusText
    }
    throw new Error(detail);
  }
  return resp.json();
}

// --- auth + admin (users / roles) ---
export function login(username, password, workspace) {
  return fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, workspace: workspace || null }),
  }).then(asJson);
}

export function logout() {
  return fetch("/api/logout", { method: "POST" }).then(asJson);
}

export function getMe() {
  return fetch("/api/me").then(asJson);
}

export function listUsers() {
  return fetch("/api/users").then(asJson);
}

export function createUser(body) {
  return fetch("/api/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(asJson);
}

export function setUserRole(id, role) {
  return fetch(`/api/users/${encodeURIComponent(id)}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  }).then(asJson);
}

// Full edit for the Users admin table: roles, admin flag, and an optional password reset (omit/blank
// `password` to leave it unchanged).
// `roles` is a list: a user may hold several roles, and gets the combined access of all of them.
export function updateUser(id, { roles, isAdmin, password }) {
  return fetch(`/api/users/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ roles: roles || [], isAdmin: !!isAdmin, password: password || null }),
  }).then(asJson);
}

export function deleteUser(id) {
  return fetch(`/api/users/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

export function listRoles() {
  return fetch("/api/roles").then(asJson);
}

export function listTables() {
  return fetch("/api/tables").then(asJson);
}

// `columns` is {table: [column, ...]} and narrows a granted table to those columns. A granted table
// omitted from it keeps whole-table access, which is the default.
// `docTags` are the knowledge-base document tags the role may read (Documents page).
export function createRole(name, tables, columns = {}, docTags = []) {
  return fetch("/api/roles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, tables, columns, docTags }),
  }).then(asJson);
}

export function updateRole(id, tables, columns = {}, docTags) {
  return fetch(`/api/roles/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tables, columns, docTags }),
  }).then(asJson);
}

// --- knowledge base (Documents page) ---
const jsonBody = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

export function listDocCategories() {
  return fetch("/api/doc-categories").then(asJson);
}

// `parentId` set = a sub-category of that category.
export function createDocCategory(name, description = "", parentId = null) {
  return fetch("/api/doc-categories", jsonBody("POST", { name, description, parentId })).then(asJson);
}

export function updateDocCategory(id, changes) {
  return fetch(`/api/doc-categories/${encodeURIComponent(id)}`, jsonBody("PUT", changes)).then(asJson);
}

export function deleteDocCategory(id) {
  return fetch(`/api/doc-categories/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

export function listDocuments() {
  return fetch("/api/documents").then(asJson);
}

// The file is read, split and embedded before this resolves, so the result is the indexed document.
export function uploadDocument(file, title, folderId) {
  const form = new FormData();
  form.append("file", file);
  form.append("title", title || "");
  form.append("folderId", folderId);
  return fetch("/api/documents", { method: "POST", body: form }).then(asJson);
}

// A new version of an existing document: same title and place; its updated date changes.
export function replaceDocumentFile(id, file) {
  const form = new FormData();
  form.append("file", file);
  return fetch(`/api/documents/${encodeURIComponent(id)}/file`, { method: "POST", body: form }).then(asJson);
}

// `changes` = { title?, folderId? } — folderId moves it to another category / sub-category.
export function updateDocument(id, changes) {
  return fetch(`/api/documents/${encodeURIComponent(id)}`, jsonBody("PUT", changes)).then(asJson);
}

export function deleteDocument(id) {
  return fetch(`/api/documents/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

export function deleteRole(id) {
  return fetch(`/api/roles/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

// --- table catalog (the semantic layer list_fields reads from) ---
export function listDbTables() {
  return fetch("/api/db-tables").then(asJson);
}

export function listTableDescriptions() {
  return fetch("/api/table-descriptions").then(asJson);
}

export function createTableDescription(tableName, description) {
  return fetch("/api/table-descriptions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tableName, description }),
  }).then(asJson);
}

export function updateTableDescription(id, description) {
  return fetch(`/api/table-descriptions/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description }),
  }).then(asJson);
}

export function deleteTableDescription(id) {
  return fetch(`/api/table-descriptions/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

export function listDbColumns(tableName) {
  return fetch(`/api/db-columns/${encodeURIComponent(tableName)}`).then(asJson);
}

export function listColumnDescriptions(tableName) {
  const qs = tableName ? `?table=${encodeURIComponent(tableName)}` : "";
  return fetch(`/api/column-descriptions${qs}`).then(asJson);
}

// One save action: an empty description clears it, anything else creates or updates it.
export function upsertColumnDescription(tableName, columnName, description) {
  return fetch(`/api/column-descriptions/${encodeURIComponent(tableName)}/${encodeURIComponent(columnName)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description }),
  }).then(asJson);
}

export function listAgents() {
  return fetch("/api/agent/agents").then(asJson);
}

export function listConversations() {
  return fetch("/api/agent/conversations").then(asJson);
}

export function getConversation(id) {
  return fetch(`/api/agent/conversations/${encodeURIComponent(id)}`).then(asJson);
}

export function deleteConversation(id) {
  return fetch(`/api/agent/conversations/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

export function renameConversation(id, title) {
  return fetch(`/api/agent/conversations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  }).then(asJson);
}

export function listConversationWidgets(id) {
  return fetch(`/api/agent/conversations/${encodeURIComponent(id)}/widgets`).then(asJson);
}

export function listDashboards() {
  return fetch("/api/dashboards").then(asJson);
}

export function getDashboard(id) {
  return fetch(`/api/dashboards/${encodeURIComponent(id)}`).then(asJson);
}

/**
 * Save a new dashboard.
 *
 * `widgets` is the set the user actually ticked in the builder, sent as the finished specs the
 * preview already holds. Passing them makes the save a plain INSERT: without them the server has to
 * reload each conversation's checkpoint and RE-RUN every stored query a second time, duplicating
 * work the preview just did (see app/dashboards.py save_dashboard). Omit it and that older,
 * rebuild-everything path still runs — which is what mobile and any add-widgets flow rely on.
 */
export function createDashboard(title, chatIds, widgets) {
  return fetch("/api/dashboards", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(widgets ? { title, chatIds, widgets } : { title, chatIds }),
  }).then(asJson);
}

// Re-runs each widget's saved query server-side. With no date window the fresh numbers are persisted;
// with one, the response is a filtered view only (see app/dashboards.py refresh_dashboard).
export function refreshDashboard(id, { dateFrom = "", dateTo = "", dateField = "" } = {}) {
  return fetch(`/api/dashboards/${encodeURIComponent(id)}/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dateFrom, dateTo, dateField }),
  }).then(asJson);
}

// Remove one card from a saved dashboard. Returns the remaining widgets.
// Remove one card (by position) from a KPI widget. Removing its last card removes the widget.
export function deleteKpiCard(dashboardId, widgetId, index) {
  return fetch(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}/cards/${index}`,
    { method: "DELETE" }
  ).then(asJson);
}

export function deleteDashboardWidget(dashboardId, widgetId) {
  return fetch(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}`,
    { method: "DELETE" }
  ).then(asJson);
}

// Append the charts/tables from further conversations to an existing dashboard.
export function addDashboardWidgets(dashboardId, chatIds) {
  return fetch(`/api/dashboards/${encodeURIComponent(dashboardId)}/widgets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chatIds }),
  }).then(asJson);
}

// The Competitive tab's quarterly figures: competitors.csv, re-read on every call (app/competitors.py).
export function getCompetitors() {
  return fetch("/api/competitors").then(asJson);
}

// Official NSE closing prices for every company in competitors.csv over one period. The first request
// for a period downloads NSE's daily files and can take several seconds; the server caches them after.
export function getCompetitorPrices(dateFrom, dateTo) {
  const qs = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  return fetch(`/api/competitors/prices?${qs}`).then(asJson);
}

// Every role, whether this dashboard is shared with it, and whether it can be (a role missing a table
// or column the dashboard uses is not eligible; `missing` says what). Admin + owner only.
export function getDashboardSharing(id) {
  return fetch(`/api/dashboards/${encodeURIComponent(id)}/sharing`).then(asJson);
}

// Share with exactly `roles` ([] makes the dashboard private again).
export function setDashboardSharing(id, roles) {
  return fetch(`/api/dashboards/${encodeURIComponent(id)}/sharing`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ roles }),
  }).then(asJson);
}

// Download the dashboard as .xlsx — the date-filtered view when a range is given, matching the screen.
export async function downloadDashboardXlsx(id, { dateFrom = "", dateTo = "" } = {}) {
  const qs = new URLSearchParams({ dateFrom, dateTo });
  return saveResponse(await fetch(`/api/dashboards/${encodeURIComponent(id)}/export.xlsx?${qs}`), "dashboard.xlsx");
}

// Download the dashboard as a "pdf" or "pptx" report: KPI cards, every chart and table, and their
// narration. `images` ({widgetId: PNG data URL}) are the charts as the browser drew them, for the PDF;
// the PowerPoint builds its own editable charts from the data.
export async function downloadDashboardReport(id, format, { dateFrom = "", dateTo = "", images = {} } = {}) {
  const resp = await fetch(`/api/dashboards/${encodeURIComponent(id)}/export/${format}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dateFrom, dateTo, images }),
  });
  return saveResponse(resp, `dashboard.${format}`);
}

async function saveResponse(resp, fallbackName) {
  if (!resp.ok) return asJson(resp); // throws with the server's message
  const name = /filename="([^"]+)"/.exec(resp.headers.get("Content-Disposition") || "")?.[1] || fallbackName;
  const url = URL.createObjectURL(await resp.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- widget builder (app/builder.py, services/query_builder.py) ---

// Tables the builder offers, with the columns this user may see: [{key, columns: [{name, kind}], dateField}].
export function builderDatasets() {
  return fetch("/api/builder/datasets").then(asJson);
}

// Run a builder query ({mode: "build"|"sql"|"prompt", build?, sql?}) and get its first rows + a suggested view.
export function builderPreview(query, { dateFrom = "", dateTo = "" } = {}) {
  return fetch("/api/builder/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, dateFrom, dateTo }),
  }).then(asJson);
}

// SQL for a plain-language description, written by the model and test-run: {sql, error?}.
export function builderGenerate(prompt, { model, apiKey } = {}) {
  return fetch("/api/builder/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt, model: model || null, apiKey: apiKey || null }),
  }).then(asJson);
}

// Save a builder widget onto a dashboard (or replace one, with widgetId). Returns {widget, widgets}.
export function saveCustomWidget(dashboardId, { title, query, view }, widgetId = null) {
  const base = `/api/dashboards/${encodeURIComponent(dashboardId)}/custom-widgets`;
  return fetch(widgetId ? `${base}/${encodeURIComponent(widgetId)}` : base, {
    method: widgetId ? "PUT" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, query, view }),
  }).then(asJson);
}

// An empty dashboard, to fill with the widget builder.
export function createBlankDashboard(title) {
  return fetch("/api/dashboards/blank", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  }).then(asJson);
}

export function deleteDashboard(id) {
  return fetch(`/api/dashboards/${encodeURIComponent(id)}`, { method: "DELETE" }).then(asJson);
}

export function renameDashboard(id, title) {
  return fetch(`/api/dashboards/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  }).then(asJson);
}

// title === "" clears the override, so the card falls back to its sourceTitle/spec.title again.
/**
 * Persist the order the owner dragged the cards into. `widgetIds` is the grid's order, which may omit
 * widgets the grid never renders (an old curator-built dashboard's summary entry) — the server slots
 * the named ones back between themselves and leaves the rest where they are.
 */
export function reorderDashboardWidgets(dashboardId, widgetIds) {
  return fetch(`/api/dashboards/${encodeURIComponent(dashboardId)}/widgets/order`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ widgetIds }),
  }).then(asJson);
}

// Save the Key metrics card order, as card keys (see KpiWidget's cardKey).
export function setKpiOrder(dashboardId, keys) {
  return fetch(`/api/dashboards/${encodeURIComponent(dashboardId)}/kpi-order`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ keys }),
  }).then(asJson);
}

export function renameDashboardWidget(dashboardId, widgetId, title) {
  return fetch(
    `/api/dashboards/${encodeURIComponent(dashboardId)}/widgets/${encodeURIComponent(widgetId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    }
  ).then(asJson);
}

// Streams one turn as parsed SSE frames: { event, data } with `data` already JSON.parsed where the
// server sends JSON, or left as a raw string for `token` events (plain text chunks, not JSON).
// `agent` is intentionally never sent — the supervisor decides per turn; see chat_service.stream_turn.
export function listModels() {
  return fetch("/api/agent/models").then(asJson);
}

// POST-and-consume-SSE. Kept as its own helper (rather than folded into streamChat) because the error
// handling here — surfacing `detail` and firing auth-expired — is the same for any future SSE POST.
async function postSSE(url, body, { signal } = {}) {
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) {
    if (resp.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth-expired"));
    }
    let detail = resp.statusText;
    try {
      const d = (await resp.json()).detail;
      detail = (Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d) || detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return resp;
}

async function* consumeSSE(resp, jsonEvents) {
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    buf = buf.replace(/\r\n/g, "\n");
    const frames = buf.split("\n\n");
    buf = frames.pop();
    for (const frame of frames) {
      let event = "message";
      const dataLines = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
      }
      const raw = dataLines.join("\n");
      if (!event || raw === undefined) continue;
      const data = jsonEvents.has(event) ? safeParse(raw) : raw;
      yield { event, data };
    }
  }
}

const CHAT_JSON_EVENTS = new Set(["status", "agent", "tool", "chart", "kpi", "table", "error", "done"]);

export async function* streamChat(conversationId, message, { signal, model, apiKey } = {}) {
  const resp = await postSSE("/api/agent/chat", { conversationId, message, model, apiKey }, { signal });
  yield* consumeSSE(resp, CHAT_JSON_EVENTS);
}

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

// --- database connections + catalog explorer (admin) ---
const jsonInit = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});
const qs = (o) => new URLSearchParams(o).toString();

export const listConnectionKinds = () => fetch("/api/connections/kinds").then(asJson);
export const listConnections = () => fetch("/api/connections").then(asJson);
export const testConnection = (spec) => fetch("/api/connections/test", jsonInit("POST", spec)).then(asJson);
export const createConnection = (spec) => fetch("/api/connections", jsonInit("POST", spec)).then(asJson);
export const deleteConnection = (id) => fetch(`/api/connections/${id}`, { method: "DELETE" }).then(asJson);
export const listConnectionDatabases = (id) => fetch(`/api/connections/${id}/databases`).then(asJson);
export const getConnectionTree = (id, database) => fetch(`/api/connections/${id}/tree?${qs({ database })}`).then(asJson);
export const getConnectionColumns = (id, database, schema, table) =>
  fetch(`/api/connections/${id}/columns?${qs({ database, schema, table })}`).then(asJson);
export const previewConnectionTable = (id, database, schema, table, limit = 50) =>
  fetch(`/api/connections/${id}/preview?${qs({ database, schema, table, limit })}`).then(asJson);
export const listSqliteFiles = () => fetch("/api/connections/sqlite-files").then(asJson);
export const startImport = (id, body) => fetch(`/api/connections/${id}/import`, jsonInit("POST", body)).then(asJson);
export const listImports = () => fetch("/api/connections/imports").then(asJson);
export const refreshImport = (id) => fetch(`/api/connections/imports/${id}/refresh`, { method: "POST" }).then(asJson);
export const deleteImport = (id) => fetch(`/api/connections/imports/${id}`, { method: "DELETE" }).then(asJson);

export const listLlmProviders = () => fetch("/api/llm/providers").then(asJson);
export const connectLlmProvider = (body) => fetch("/api/llm/providers", jsonInit("POST", body)).then(asJson);
export const setLlmModels = (id, enabled) => fetch(`/api/llm/providers/${id}/models`, jsonInit("PUT", { enabled })).then(asJson);
export const testLlmModel = (id, model) => fetch(`/api/llm/providers/${id}/test`, jsonInit("POST", { model })).then(asJson);
export const deleteLlmProvider = (id) => fetch(`/api/llm/providers/${id}`, { method: "DELETE" }).then(asJson);

export const listMlDatasets = () => fetch("/api/ml/datasets").then(asJson);
export const profileMl = (dataset, target) => fetch("/api/ml/profile", jsonInit("POST", { dataset, target })).then(asJson);
export const listMlModels = () => fetch("/api/ml/models").then(asJson);
export const createMlModel = (body) => fetch("/api/ml/models", jsonInit("POST", body)).then(asJson);
export const getMlModel = (id) => fetch(`/api/ml/models/${id}`).then(asJson);
export const retrainMlModel = (id) => fetch(`/api/ml/models/${id}/retrain`, { method: "POST" }).then(asJson);
export const predictMl = (id, rows) => fetch(`/api/ml/models/${id}/predict`, jsonInit("POST", { rows })).then(asJson);
export const setMlSchedule = (id, schedule) => fetch(`/api/ml/models/${id}/schedule`, jsonInit("PUT", { schedule })).then(asJson);
export const scoreMl = (id) => fetch(`/api/ml/models/${id}/score`, { method: "POST" }).then(asJson);
export const deleteMlModel = (id) => fetch(`/api/ml/models/${id}`, { method: "DELETE" }).then(asJson);

export const getAgentRoster = () => fetch("/api/ops/agents").then(asJson);
export const getAgentStats = (days = 7) => fetch(`/api/ops/stats?${qs({ days })}`).then(asJson);
export const listAgentRuns = (params) => fetch(`/api/ops/runs?${qs(params)}`).then(asJson);
export const getAgentRun = (id) => fetch(`/api/ops/runs/${id}`).then(asJson);
export const jobOptions = () => fetch("/api/ops/jobs/options").then(asJson);
export const listJobs = () => fetch("/api/ops/jobs").then(asJson);
export const getJob = (id) => fetch(`/api/ops/jobs/${id}`).then(asJson);
export const createJob = (body) => fetch("/api/ops/jobs", jsonInit("POST", body)).then(asJson);
export const updateJob = (id, body) => fetch(`/api/ops/jobs/${id}`, jsonInit("PUT", body)).then(asJson);
export const deleteJob = (id) => fetch(`/api/ops/jobs/${id}`, { method: "DELETE" }).then(asJson);
export const runJob = (id) => fetch(`/api/ops/jobs/${id}/run`, { method: "POST" }).then(asJson);
export const cancelJob = (id) => fetch(`/api/ops/jobs/${id}/cancel`, { method: "POST" }).then(asJson);

const post = (url, body) => fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(asJson);
export const platformConfig = () => fetch("/api/platform/config").then(asJson);
export const signup = (body) => post("/api/platform/signup", body);
export const ownerLogin = (username, password) => post("/api/platform/owner/login", { username, password });
export const ownerLogout = () => post("/api/platform/owner/logout", {});
export const ownerMe = () => fetch("/api/platform/owner/me").then(asJson);
export const ownerWorkspaces = () => fetch("/api/platform/owner/workspaces").then(asJson);
export const ownerCreateWorkspace = (body) => post("/api/platform/owner/workspaces", body);
export const ownerUpdateWorkspace = (slug, body) =>
  fetch(`/api/platform/owner/workspaces/${slug}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(asJson);
export const ownerDeleteWorkspace = (slug, confirm) =>
  fetch(`/api/platform/owner/workspaces/${slug}?${new URLSearchParams({ confirm })}`, { method: "DELETE" }).then(asJson);
